import React from "react";
import { Modal } from "../../Components/Modal";
import TaxonomyMultiSelect from "../../Components/TaxonomyMultiSelect";
import type { UploadKind, UploadFormState, Step } from "./types";
import { UPLOAD_OPTIONS } from "./types";
import { ImageUploadSection, PublicationUploadSection } from "./UploadSections";

interface UploadModalProps {
	modalOpen: boolean;
	submitting: boolean;
	currentStep: Step;
	selection: UploadKind;
	form: UploadFormState;
	previewUrl: string | null;
	previewType: "image" | "pdf" | "other" | null;
	canAdvance: boolean;
	canSubmit: boolean;
	submitError: string | null;
	successMessage: string | null;
	taxonomies: any[];
	steps: Array<{ id: Step; label: string }>;
	onClose: () => void;
	onNext: () => void;
	onBack: () => void;
	onSubmit: () => void;
	onSelectionChange: (kind: UploadKind) => void;
	onFileChange: (file: File | null) => void;
	onTitleChange: (value: string) => void;
	onInputChange: (key: keyof UploadFormState, value: string | string[] | File | null) => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({
	modalOpen,
	submitting,
	currentStep,
	selection,
	form,
	previewUrl,
	previewType,
	canAdvance,
	canSubmit,
	submitError,
	successMessage,
	taxonomies,
	steps,
	onClose,
	onNext,
	onBack,
	onSubmit,
	onSelectionChange,
	onFileChange,
	onTitleChange,
	onInputChange
}) => {
	const renderStepContent = () => {
		if (currentStep === 1) {
			return (
				<section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm min-h-[380px]">
					<header className="mb-4">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step 1 · Destination</p>
						<h2 className="mt-1 text-xl font-semibold text-slate-900">Choose what you want to upload</h2>
					</header>
					<div className="grid gap-4 md:grid-cols-2">
						{UPLOAD_OPTIONS.map((option) => (
							<button
								type="button"
								key={option.id}
								onClick={() => onSelectionChange(option.id)}
								className={`text-left rounded-2xl border px-5 py-4 transition ${
									selection === option.id ? "border-slate-900 bg-slate-900 text-white shadow-lg" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
								}`}
							>
								<p className="text-sm font-semibold uppercase tracking-wide">{option.title}</p>
								<p className="mt-1 text-sm">{option.description}</p>
								<p className="mt-2 text-xs uppercase tracking-wide text-slate-400">
									{selection === option.id ? "Selected destination" : option.helper}
								</p>
							</button>
						))}
					</div>
				</section>
			);
		}

		if (currentStep === 2) {
			return selection === "image" ? (
				<ImageUploadSection form={form} onFileChange={onFileChange} previewUrl={previewUrl} previewType={previewType} />
			) : (
				<PublicationUploadSection form={form} onFileChange={onFileChange} previewUrl={previewUrl} previewType={previewType} />
			);
		}

		return (
			<section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm min-h-[420px]">
				<header className="mb-4">
					<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step 3 · Details</p>
					<h2 className="mt-1 text-xl font-semibold text-slate-900">Describe the asset for future use</h2>
				</header>
				<div className="grid gap-4 lg:grid-cols-2">
					<div>
						<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Title</label>
						<input
							type="text"
							className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
							placeholder="Hero illustration"
							value={form.title}
							onChange={(event) => onTitleChange(event.target.value)}
						/>
					</div>
					<div>
						<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">File name</label>
						<input
							type="text"
							className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
							placeholder="hero-landing"
							value={form.fileName}
							onChange={(event) => onInputChange("fileName", event.target.value)}
						/>
						<p className="mt-1 text-xs text-slate-400">Extension is pulled from the uploaded file automatically.</p>
					</div>
				</div>
				<div className="mt-4 grid gap-4 lg:grid-cols-2">
					<div>
						<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Folder</label>
						<input
							type="text"
							className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
							placeholder="campaigns/summer"
							value={form.folder}
							onChange={(event) => onInputChange("folder", event.target.value)}
						/>
					</div>
					<div>
						<TaxonomyMultiSelect
							label="Taxonomies"
							options={taxonomies}
							value={form.taxonomyIds}
							onChange={(next) => onInputChange("taxonomyIds", next)}
							placeholder="Assign taxonomies"
							helper="Select one or more taxonomies for this asset."
						/>
					</div>
				</div>
				{submitError && <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{submitError}</p>}
				{successMessage && <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{successMessage}</p>}
			</section>
		);
	};

	return (
		<Modal show={modalOpen} onHide={onClose} size="full85" centered>
			<Modal.Header closeButton>
				<div className="space-y-1">
					<Modal.Title>Upload an asset</Modal.Title>
					<p className="text-sm text-slate-500">Work through each step to finish the upload.</p>
				</div>
			</Modal.Header>
			<Modal.Body className="bg-slate-50">
				<div className="flex h-full flex-col gap-6">
					<div className="flex flex-wrap items-center gap-3">
						{steps.map((step, index) => (
							<div key={step.id} className="flex items-center gap-2">
								<div
									className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
										currentStep === step.id
											? "bg-slate-900 text-white"
											: currentStep > step.id
												? "bg-slate-200 text-slate-700"
												: "border border-slate-200 bg-white text-slate-400"
									}`}
								>
									{step.id}
								</div>
								<span className={`text-sm font-medium ${currentStep === step.id ? "text-slate-900" : "text-slate-500"}`}>{step.label}</span>
								{index < steps.length - 1 && <span className="text-slate-400">/</span>}
							</div>
						))}
					</div>
					<div className="flex-1">{renderStepContent()}</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onClose}
					disabled={submitting}
					className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Cancel
				</button>
				{currentStep > 1 && (
					<button
						type="button"
						onClick={onBack}
						disabled={submitting}
						className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						Back
					</button>
				)}
				{currentStep < 3 && (
					<button
						type="button"
						onClick={onNext}
						disabled={!canAdvance}
						className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						Next
					</button>
				)}
				{currentStep === 3 && (
					<button
						type="button"
						onClick={onSubmit}
						disabled={!canSubmit || submitting}
						className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						{submitting ? "Uploading\u2026" : "Upload"}
					</button>
				)}
			</Modal.Footer>
		</Modal>
	);
};
